import { TFunction } from "i18next";
import type { SnakeCasedPropertiesDeep } from "type-fest";

export type AssetExportStatus = "STARTED" | "SUCCEEDED" | "WARNING" | "FAILED";

export interface AssetExportJobDetail {
    jobUuid: string;
    status: AssetExportStatus;
    totalRequestedAssets: number;
    totalExportedAssets: number;
    totalFailedAssets: number;
    tenantUuid: string;
    creationDate: string;
    presignedUrl?: string;
    linkExpirationDate?: string;
}

export type AssetExportJobDetailDto = SnakeCasedPropertiesDeep<AssetExportJobDetail>;

export function toAssetExportJobDetail(dto: AssetExportJobDetailDto): AssetExportJobDetail {
    return {
        jobUuid: dto.job_uuid,
        status: dto.status,
        totalRequestedAssets: dto.total_requested_assets,
        totalExportedAssets: dto.total_exported_assets,
        totalFailedAssets: dto.total_failed_assets,
        tenantUuid: dto.tenant_uuid,
        creationDate: dto.creation_date,
        presignedUrl: dto.presigned_url,
        linkExpirationDate: dto.link_expiration_date,
    };
}

const parseSentToEmail = (data: string | undefined): boolean => {
    try {
        return JSON.parse(data ?? "").sent_to_email === true;
    } catch {
        return false;
    }
};

export function getAssetExportTitleKey(
    notificationType: string | undefined,
    notificationData: string | undefined,
    t: TFunction
): string {
    const isSentToEmail = parseSentToEmail(notificationData);

    switch (notificationType) {
        case "ASSET_EXPORT_STARTED":
            return isSentToEmail
                ? t("Notification.assetExportNotifications.inProgressEmailTitle")
                : t("Notification.assetExportNotifications.inProgressTitle");
        case "ASSET_EXPORT_FAILED":
            return isSentToEmail
                ? t("Notification.assetExportNotifications.failureEmailTitle")
                : t("Notification.assetExportNotifications.failureTitle");
        case "ASSET_EXPORT_SUCCEEDED":
            return isSentToEmail
                ? t("Notification.assetExportNotifications.successEmailTitle")
                : t("Notification.assetExportNotifications.successTitle");
        case "ASSET_EXPORT_WARNING":
            return isSentToEmail
                ? t("Notification.assetExportNotifications.successWithFailEmailTitle")
                : t("Notification.assetExportNotifications.successWithFailTitle");
        default:
            return "";
    }
}
